/**
 * Shared sub-header status bar for simulation modules.
 *
 * Sits immediately below the global `ExamHeader` and hosts module-specific
 * status content — adaptive badges, audio waveform, review message, etc. —
 * in a consistent strip so Reading/Listening/Writing align vertically.
 *
 * The bar is a thin chrome element: it has no state, no effects, and defers
 * all logic to children.
 */

import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

export interface SimulationModuleStatusBarProps {
  /** Left-aligned content (e.g. adaptive state badge). */
  readonly left?: ReactNode;
  /** Center content (e.g. audio waveform, section title). */
  readonly center?: ReactNode;
  /** Right-aligned content (e.g. Overview button). */
  readonly right?: ReactNode;
  /** Extra classes appended to the root element. */
  readonly className?: string;
}

export function SimulationModuleStatusBar({
  left,
  center,
  right,
  className,
}: SimulationModuleStatusBarProps) {
  return (
    <div
      className={cn(
        "flex shrink-0 items-center justify-between gap-2 border-b border-border bg-muted/20 px-3 py-1 sm:gap-3 sm:px-4 sm:py-1.5",
        className,
      )}
    >
      <div className="flex items-center gap-2 min-w-0">{left ?? <span />}</div>
      <div className="flex-1 flex min-w-0 w-full items-stretch justify-stretch">
        {center == null ? null : (
          <div className="w-full min-w-0 [&>*]:w-full">{center}</div>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0 justify-end">
        {right ?? <span />}
      </div>
    </div>
  );
}
